import { ApprovalableTypeEnum } from 'api-hooks/common';
import { getRefundsKey } from 'api-hooks/refund/refund';
import {
  getRefundReleaseUserKey,
  getRefundReleaseUsersKey,
  RefundReleaseUserModel,
} from 'api-hooks/refund/refund-release-user';
import {
  useCheckApprovalRefundReleaseUser,
  useReleasingRefundReleaseUser,
} from 'api-hooks/refund/refund-release-user/mutation';
import { CheckboxCheckedSVG } from 'common/assets';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { Form } from 'components/elements';
import { Button } from 'components/elements/buttons';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useDialog from 'hooks/use-dialog';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import { isDataActive } from 'hooks/use-void-action';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import TitleStatus from 'legacy-modules/common/title-status';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import CustomerDetailField from './customer-detail-field';
import InformationField from './information-field';
import PaymentField from './payment-field';
import { FormContent, FormDivider } from './styles';

interface Props {
  refundReleaseUser?: RefundReleaseUserModel;
}

export default function RefundReleaseUserForm(props: Props) {
  const { refundReleaseUser } = props;

  const dialog = useDialog();
  const { t } = useTranslation();

  const { mutateAsync: mutateReleasing, isLoading: isLoadingReleasing } =
    useReleasingRefundReleaseUser();
  const { mutateAsync, isLoading } = useCheckApprovalRefundReleaseUser();

  const defaultValues = React.useMemo(() => {
    return {
      invoiceNumber: refundReleaseUser?.sale.invoiceNumber,
      refundAmount: refundReleaseUser?.amount,
      number: refundReleaseUser?.number,
      reason: refundReleaseUser?.reason,
      requestedAt: refundReleaseUser?.requestedAt,
      paymentMethodId: refundReleaseUser?.paymentMethod.id,
      paymentName: refundReleaseUser?.paymentMethod.type,
      paymentableId: refundReleaseUser?.paymentable?.chequeGiro?.id
        ? refundReleaseUser.paymentable.chequeGiro.id
        : refundReleaseUser?.paymentable.id,
      customerId: refundReleaseUser?.customer.id,
      chequeNote: refundReleaseUser?.paymentable.chequeGiro
        ? {
            referenceNumber: refundReleaseUser.paymentable.referenceNumber,
            issuedAt: refundReleaseUser.paymentable.issuedAt,
            expiredAt: refundReleaseUser.paymentable.expiredAt,
            availableAt: refundReleaseUser.paymentable.availableAt,
            bankAccountId: refundReleaseUser.paymentable.bankAccount.id,
          }
        : null,
      sales: {
        id: refundReleaseUser?.sale.id,
        transactionType: refundReleaseUser?.sale.transactionType,
        code: refundReleaseUser?.sale.code,
        description: refundReleaseUser?.sale.description,
        transactionAt: refundReleaseUser?.sale.transactionAt,
        total: refundReleaseUser?.sale.total,
        adminId: refundReleaseUser?.sale.admin.id,
        operatorId: refundReleaseUser?.sale.operator?.id,
        salesmanId: refundReleaseUser?.sale.salesman?.id,
      },
    };
  }, [refundReleaseUser]);

  const methods = useForm({
    defaultValues,
  });

  const onClickReleaseRequest = React.useCallback(async () => {
    try {
      const checkResult = await mutateAsync({
        releaseSaleId: refundReleaseUser!.id,
      });
      if (checkResult.data.approval) {
        dialog.showCustom({
          render: (close) => (
            <RequestApprovalModal
              onClose={close}
              data={{
                approvalableId: refundReleaseUser?.id!,
                approvalableType: ApprovalableTypeEnum.ReleaseSale,
              }}
              onSubmit={async ({ createApproval, form, input }) => {
                const result = await createApproval(input);
                notification.success({ message: result?.message });

                queryClient.refetchQueries([getRefundReleaseUsersKey()[0]]);
                queryClient.refetchQueries(
                  getRefundReleaseUserKey({ id: refundReleaseUser?.id! }),
                );
                queryClient.refetchQueries([getRefundsKey()[0]]);
                close();
              }}
            />
          ),
        });
      } else {
        dialog.showConfirmation({
          title: t('common:confirmation'),
          message: t('common:confirmation_text'),
          onNegativeAction: (dismiss) => dismiss(),
          onPositiveAction: async (dismiss) => {
            dismiss();
            try {
              await mutateReleasing({ releaseSaleId: refundReleaseUser!.id });
              queryClient.refetchQueries([getRefundReleaseUsersKey()[0]]);
              queryClient.refetchQueries(
                getRefundReleaseUserKey({ id: refundReleaseUser!.id }),
              );
              queryClient.refetchQueries([getRefundsKey()[0]]);
            } catch (error) {
              notification.error(error.message);
            }
          },
        });
      }
    } catch (error) {
      notification.error(error.message);
    }
  }, [dialog, mutateAsync, mutateReleasing, refundReleaseUser, t]);

  const isActive = isDataActive(refundReleaseUser!.status);
  const { requestApprovalButton } = useApprovalAction(
    'release-sales',
    refundReleaseUser,
  );
  const { resolverButton } = useResolveApproval(
    'release-sales',
    refundReleaseUser,
  );

  const { revertDraftButton } = useRevertDraftAction(
    'release-sales',
    refundReleaseUser,
  );

  const onRenderRightComponent = React.useMemo(() => {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
        {revertDraftButton}
        {isActive && (
          <Button
            buttonType="primary"
            startEnhancer={(color, size) => (
              <CheckboxCheckedSVG width={size} height={size} color={color} />
            )}
            onClick={onClickReleaseRequest}
            isLoading={isLoadingReleasing || isLoading}
          >
            {t('refund:request_release')}
          </Button>
        )}
        {requestApprovalButton}
        {resolverButton}
      </div>
    );
  }, [
    isActive,
    isLoading,
    isLoadingReleasing,
    onClickReleaseRequest,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    t,
  ]);

  const customViewName =
    refundReleaseUser &&
    [t('common:refund_release_user'), refundReleaseUser.number].join(' - ');

  return (
    <Form
      methods={methods}
      onSubmit={() => {}}
      defaultEditable={!refundReleaseUser}
      customViewName={customViewName}
    >
      <FormHeader
        data={refundReleaseUser}
        title={
          refundReleaseUser ? (
            <TitleStatus
              type="release"
              title={customViewName ?? t('common:refund_release_user')}
              status={refundReleaseUser.status}
            />
          ) : (
            t('common:add_extra', { extra: t('common:refund_release_user') })
          )
        }
        noSubmit
        noDelete
        noEdit
        onRenderRightComponent={onRenderRightComponent}
      />
      <FormContent>
        <div>
          <InformationField />
          <PaymentField />
          <CustomerDetailField />
          <FormDivider />
        </div>
      </FormContent>
    </Form>
  );
}
